import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { CellContext } from "@tanstack/react-table";
import { AlertCircleIcon, CheckCircleIcon, LoaderIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

enum State {
  PENDING = "pending",
  EXPORTED = "exported",
  FAILED = "failed",
}

type StatusItemProps = {
  label: string;
  variant: "success" | "loading" | "error";
};

export const StateItem = ({ label, variant }: StatusItemProps) => {
  const styles = {
    error: {
      icon: <AlertCircleIcon className="h-4 w-4 text-red-500" />,
      className: "bg-red-100 text-red-800",
    },
    success: {
      icon: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
      className: "bg-green-100 text-green-800",
    },
    loading: {
      icon: <LoaderIcon className="h-4 w-4 text-yellow-500 animate-spin" />,
      className: "bg-yellow-100 text-yellow-800",
    },
  };

  return (
    <Badge variant="outline" className={cn("gap-1", styles[variant].className)}>
      {styles[variant].icon}
      {label}
    </Badge>
  );
};

export const StateCell = ({ cell }: CellContext<any, unknown>) => {
  const { t } = useTranslation();
  const { row } = cell;

  const state = row.original.state;

  return (
    <>
      {state === State.PENDING && (
        <StateItem label={t("exports.states.pending")} variant="loading" />
      )}
      {state === State.FAILED && (
        <StateItem label={t("exports.states.failed")} variant="error" />
      )}
      {state === State.EXPORTED && (
        <StateItem label={t("exports.states.exported")} variant="success" />
      )}
    </>
  );
};
